import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type Curso = {
  id: string;
  title: string;
  description: string | null;
  external_url: string | null;
  price_clp: number | null;
  image_url: string | null;
  is_featured: boolean;
};

export const Route = createFileRoute("/cursos/personas/certificacion-mine-class")({
  head: () => ({
    meta: [
      { title: "Cursos Certificación Mine Class | Mine Class" },
      {
        name: "description",
        content:
          "Cursos y diplomados con certificación Mine Class para profesionales de la minería: planificación minera, geología, contratos, Excel, Big Data y más.",
      },
      { property: "og:title", content: "Cursos Certificación Mine Class" },
      { property: "og:description", content: "Catálogo de cursos y diplomados con certificación Mine Class, con inscripción directa en umayor.mine-class.com." },
      { property: "og:type", content: "website" },
    ],
  }),
  component: Content,
});

function formatCLP(n: number) {
  return `$${n.toLocaleString("es-CL")} CLP`;
}

function Content() {
  const [items, setItems] = useState<Curso[] | null>(null);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("courses")
      .select("id,title,description,external_url,price_clp,image_url,is_featured")
      .eq("course_type", "certificacion")
      .eq("is_active", true)
      .order("is_featured", { ascending: false })
      .order("title", { ascending: true });
    setItems((data ?? []) as Curso[]);
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const channel = supabase
      .channel("courses-certificacion")
      .on("postgres_changes", { event: "*", schema: "public", table: "courses" }, () => load())
      .subscribe();
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      supabase.removeChannel(channel);
    };
  }, [load]);

  return (
    <div className="space-y-8">
      <div>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--color-brand-coral)]">
          Certificación Mine Class
        </span>
        <h2 className="text-2xl md:text-3xl font-bold text-[var(--color-brand-purple)] mt-1">
          Cursos y Diplomados con Certificación Mine Class
        </h2>
        <p className="text-[var(--color-on-surface-variant)] mt-3 text-base md:text-lg leading-relaxed">
          Catálogo 100% online para trabajadores, estudiantes y profesionales de la minería. Al comprar
          serás redirigido a nuestra plataforma <strong>umayor.mine-class.com</strong> para completar la inscripción y el pago.
        </p>
      </div>

      {items === null ? (
        <p className="text-[var(--color-on-surface-variant)]">Cargando cursos…</p>
      ) : items.length === 0 ? (
        <p className="text-[var(--color-on-surface-variant)]">No hay cursos disponibles por el momento.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((c) => (
            <article
              key={c.id}
              className="flex flex-col bg-white border border-[var(--color-outline-variant)] rounded-md overflow-hidden hover:border-[var(--color-brand-coral)] transition-colors"
            >
              {c.image_url && (
                <img src={c.image_url} alt={c.title} loading="lazy" className="w-full aspect-video object-cover" />
              )}
              <div className="p-5 flex flex-col flex-1">
                <h3 className="text-base font-bold text-[var(--color-brand-purple)] leading-snug">
                  {c.title}
                </h3>
                <p className="text-sm text-[var(--color-on-surface-variant)] mt-2 line-clamp-5 flex-1">
                  {c.description}
                </p>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-[var(--color-on-surface)]">
                    {c.price_clp != null ? formatCLP(c.price_clp) : "Consultar precio"}
                  </span>
                  {c.external_url && (
                    <a
                      href={c.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-2 bg-[var(--color-brand-coral)] text-white font-bold text-xs uppercase tracking-wider rounded-md hover:brightness-110 transition-all"
                    >
                      Comprar
                      <span className="material-symbols-outlined text-sm">arrow_outward</span>
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
